// Progress document: the single shape used by the app, the sync API and the
// server. Pure functions only, so everything here is node-testable.
//
// Every entry carries `t` (ms since epoch, set by the device that made the
// change). Documents from different devices merge per key, last write wins,
// which makes merge commutative, associative and idempotent: syncing in any
// order, any number of times, converges on the same result.

export const PROGRESS_VERSION = 1;

// Structure of the book (content/book.json + the prologue as chapter 0).
// tests/unit/shared.test.js asserts these match the content files.
export const VERSE_COUNTS = [47, 72, 43, 42, 29, 47, 30, 28, 34, 42, 55, 20, 34, 27, 20, 24, 28, 78];
export const LESSON_COUNTS = [4, 5, 8, 5, 5, 4, 5, 4, 4, 4, 4, 6, 3, 5, 4, 4, 4, 4, 9]; // index 0 = prologue

export const TOTAL_VERSES = VERSE_COUNTS.reduce((a, b) => a + b, 0);
export const TOTAL_LESSONS = LESSON_COUNTS.reduce((a, b) => a + b, 0);

const LIMITS = {
  notes: 2000,
  noteChars: 8000,
  days: 4000,
  quiz: 200,
};
const THEMES = ['system', 'light', 'dark'];
const TEXT_SIZES = ['s', 'm', 'l', 'xl'];
const MIN_T = Date.UTC(2024, 0, 1);

export function emptyProgress() {
  return {
    v: PROGRESS_VERSION,
    resetAt: 0,
    verses: {},
    lessons: {},
    quiz: {},
    bookmarks: {},
    notes: {},
    days: {},
    settings: null,
    current: null,
    profile: null,
  };
}

export function isVerseId(id) {
  const m = /^(\d{1,2})\.(\d{1,2})$/.exec(id);
  if (!m) return false;
  const ch = Number(m[1]);
  const v = Number(m[2]);
  return ch >= 1 && ch <= 18 && v >= 1 && v <= VERSE_COUNTS[ch - 1] && String(ch) === m[1] && String(v) === m[2];
}

export function isLessonId(id) {
  const m = /^(\d{1,2})-(\d{1,2})$/.exec(id);
  if (!m) return false;
  const ch = Number(m[1]);
  const n = Number(m[2]);
  return ch >= 0 && ch <= 18 && n >= 1 && n <= LESSON_COUNTS[ch] && String(ch) === m[1] && String(n) === m[2];
}

// Notes are keyed by verse id ("2.47") or by lesson reflection ("r:2-6").
export function isNoteKey(key) {
  if (key.startsWith('r:')) return isLessonId(key.slice(2));
  return isVerseId(key);
}

export function isDay(key) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return false;
  const d = new Date(`${key}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === key;
}

const isObj = (x) => x !== null && typeof x === 'object' && !Array.isArray(x);

function cleanT(t, now) {
  if (typeof t !== 'number' || !Number.isFinite(t)) return null;
  const rounded = Math.floor(t);
  if (rounded < MIN_T) return null;
  // A device with a clock in the future must not win every merge forever.
  return Math.min(rounded, now + 5 * 60 * 1000);
}

function cleanMap(input, isKey, cleanEntry, max) {
  const out = {};
  if (!isObj(input)) return out;
  let n = 0;
  for (const [k, v] of Object.entries(input)) {
    if (n >= max) break;
    if (!isKey(k) || !isObj(v)) continue;
    const e = cleanEntry(v);
    if (e) {
      out[k] = e;
      n++;
    }
  }
  return out;
}

// Returns a clean copy of `input` that contains only known, well-formed data.
// Unknown keys, bad ids and malformed entries are dropped, never trusted.
export function sanitizeProgress(input, now = Date.now()) {
  const doc = emptyProgress();
  if (!isObj(input)) return doc;
  const T = (e) => cleanT(e.t, now);
  doc.resetAt = cleanT(input.resetAt, now) || 0;

  doc.verses = cleanMap(input.verses, isVerseId, (e) => {
    const t = T(e);
    return t ? { t } : null;
  }, 1000);

  doc.lessons = cleanMap(input.lessons, isLessonId, (e) => {
    const t = T(e);
    return t ? { t } : null;
  }, 200);

  doc.quiz = cleanMap(input.quiz, isLessonId, (e) => {
    const t = T(e);
    const total = Number.isInteger(e.total) && e.total >= 0 && e.total <= 10 ? e.total : null;
    const correct = Number.isInteger(e.correct) && e.correct >= 0 ? e.correct : null;
    if (!t || total === null || correct === null || correct > total) return null;
    return { t, correct, total };
  }, LIMITS.quiz);

  doc.bookmarks = cleanMap(input.bookmarks, isVerseId, (e) => {
    const t = T(e);
    return t ? { t, on: e.on === true } : null;
  }, 1000);

  doc.notes = cleanMap(input.notes, isNoteKey, (e) => {
    const t = T(e);
    if (!t || typeof e.text !== 'string') return null;
    return { t, text: e.text.slice(0, LIMITS.noteChars) };
  }, LIMITS.notes);

  if (isObj(input.days)) {
    let n = 0;
    for (const [k, v] of Object.entries(input.days)) {
      if (n >= LIMITS.days) break;
      if (isDay(k) && Number.isInteger(v) && v > 0) {
        doc.days[k] = Math.min(v, 10000);
        n++;
      }
    }
  }

  if (isObj(input.settings)) {
    const s = input.settings;
    const t = T(s);
    if (t) {
      doc.settings = {
        t,
        theme: THEMES.includes(s.theme) ? s.theme : 'system',
        textSize: TEXT_SIZES.includes(s.textSize) ? s.textSize : 'm',
        sanskrit: s.sanskrit === true,
        iast: s.iast !== false,
        meaning: s.meaning !== false,
      };
    }
  }

  if (isObj(input.current)) {
    const c = input.current;
    const t = T(c);
    if (t && isLessonId(c.lesson) && Number.isInteger(c.step) && c.step >= 0 && c.step < 100) {
      doc.current = { t, lesson: c.lesson, step: c.step };
    }
  }

  if (isObj(input.profile)) {
    const p = input.profile;
    const t = T(p);
    if (t && typeof p.name === 'string') {
      doc.profile = { t, name: p.name.trim().slice(0, 40) };
    }
  }

  return doc;
}

// Deterministic tie-break so two devices always agree on the winner.
function newer(a, b) {
  if (!a) return b;
  if (!b) return a;
  if (a.t !== b.t) return a.t > b.t ? a : b;
  return JSON.stringify(a) >= JSON.stringify(b) ? a : b;
}

function mergeMap(a = {}, b = {}) {
  const out = { ...a };
  for (const [k, v] of Object.entries(b)) out[k] = newer(out[k], v);
  return out;
}

// "Start over" sets resetAt. Anything recorded before the newest reset is
// dropped during every merge, so a reset on one device reaches all of them
// instead of being undone by the next sync. Settings and name survive.
function since(map, resetAt) {
  if (!resetAt) return map;
  const out = {};
  for (const [k, v] of Object.entries(map)) if (v.t >= resetAt) out[k] = v;
  return out;
}

export function mergeProgress(a, b) {
  const x = a || emptyProgress();
  const y = b || emptyProgress();
  const resetAt = Math.max(x.resetAt || 0, y.resetAt || 0);
  const firstDay = resetAt ? new Date(resetAt).toISOString().slice(0, 10) : '';
  const days = {};
  for (const src of [x.days || {}, y.days || {}]) {
    for (const [k, v] of Object.entries(src)) if (k >= firstDay) days[k] = Math.max(days[k] || 0, v);
  }
  const current = newer(x.current, y.current);
  return {
    v: PROGRESS_VERSION,
    resetAt,
    verses: since(mergeMap(x.verses, y.verses), resetAt),
    lessons: since(mergeMap(x.lessons, y.lessons), resetAt),
    quiz: since(mergeMap(x.quiz, y.quiz), resetAt),
    bookmarks: since(mergeMap(x.bookmarks, y.bookmarks), resetAt),
    notes: since(mergeMap(x.notes, y.notes), resetAt),
    days,
    settings: newer(x.settings, y.settings),
    current: current && current.t >= resetAt ? current : null,
    profile: newer(x.profile, y.profile),
  };
}

// Stable serialisation, so "did anything change?" is a string comparison.
export function canonical(doc) {
  const sortKeys = (v) => {
    if (Array.isArray(v)) return v.map(sortKeys);
    if (isObj(v)) {
      const o = {};
      for (const k of Object.keys(v).sort()) o[k] = sortKeys(v[k]);
      return o;
    }
    return v;
  };
  return JSON.stringify(sortKeys(doc));
}

// ---------------------------------------------------------------------------
// Days and streaks. A "day" is always the learner's LOCAL calendar date, and
// this is the only function that turns a timestamp into one.
export function localDay(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function shiftDay(day, delta) {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

// Current streak counts back from today; a streak stays alive through today
// if the learner studied yesterday but not yet today.
export function streaks(days, today = localDay()) {
  const active = (k) => (days[k] || 0) > 0;
  let current = 0;
  let cursor = active(today) ? today : shiftDay(today, -1);
  while (active(cursor)) {
    current++;
    cursor = shiftDay(cursor, -1);
  }
  const sorted = Object.keys(days).filter(active).sort();
  let best = 0;
  let run = 0;
  let prev = null;
  for (const k of sorted) {
    run = prev && shiftDay(prev, 1) === k ? run + 1 : 1;
    best = Math.max(best, run);
    prev = k;
  }
  return { current, best: Math.max(best, current), studiedToday: active(today) };
}

// The last 7 local days ending today, oldest first.
export function lastWeek(days, today = localDay()) {
  const out = [];
  for (let i = 6; i >= 0; i--) {
    const day = shiftDay(today, -i);
    out.push({ day, active: (days[day] || 0) > 0, isToday: i === 0 });
  }
  return out;
}
